import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { GlobalComponent } from 'src/app/global-component';
import { SearchResultModel } from 'src/app/core/models/query/search-result-model';
import { CategoryModel } from 'src/app/core/models/document/category-model';
import { CategorySearchModel } from 'src/app/core/models/document/category-search-model';

// Range Slider
import { BaseListService } from 'src/app/core/services/list-services/base-list.service';
import { BlogsBaseModel } from '../../models/blogs/blogs-base.model';
import { BlogSearchModel } from '../../models/blogs/blog-search.model';
import { AiApiService } from '../ai-api.service';
import { BlogStatus } from '../../enums/blog-status-enum';


@Injectable({ providedIn: 'root' })
export class BlogListService extends BaseListService {

  defaultBlogListState: BlogSearchModel = {
    sortColumnDirective: 'createdDate',
    sortDirectionDirective: 'desc',
    pageSize: GlobalComponent.defaultPagingInfo.pageSize,
    pageIndex: 0,
    startIndex: 0,
    sortColumn: 'ModifiedDate',
    sortDirection: 'desc',
    searchTerm: '',
    categories: [],
    tags: [],
    to: undefined,
    from: undefined,
    rangeDate: undefined,
    status: BlogStatus.Published
  };

  private _resultItems$ = new BehaviorSubject<BlogsBaseModel[]>([]);

  protected override _state: BlogSearchModel = { ...this.defaultBlogListState };

  private _categories$ = new BehaviorSubject<CategoryModel[]>([]);
  private _tags$ = new BehaviorSubject<CategoryModel[]>([]);

  get categories$() { return this._categories$.asObservable(); }
  set categoryId(categoryId: any) {
    if (categoryId) {
      this._state.categories = [categoryId];
    } else {
      this._state.categories = [];
    }
    this._search$.next();
  }

  get status$() { return this._state.status; }
  set status(status: BlogStatus|undefined) {
    this._state.status = status;
    this._search$.next();
  }

  get rangeDate() { return this._state.rangeDate; };
  set rangeDate(rangeDate: any) { this._setDateSearch({ rangeDate }); }

  get tags$() { return this._tags$.asObservable(); }
  set tagId(tagId: any) {
    if (tagId) {
      this._state.tags = [tagId];
    } else {
      this._state.tags = [];
    }
    this._search$.next();
  }

  constructor(
    private aiApiService: AiApiService) {
    super();
    this._search$.pipe(
      tap(() => this._loading$.next(true)),
      switchMap(() => this._search()),
      tap(() => this._loading$.next(false))
    ).subscribe((result: any) => {
        this._resultItems$.next(result.items);
        this._total$.next(result.count);
    });

    this._getCategories().subscribe((result: any | undefined) => {
      if (result) {
        var emptyCategory = new CategoryModel();
        emptyCategory.id = "";
        emptyCategory.name = "Select Category";
        result.items = [];
        result.items.push(emptyCategory);
        result.forEach((name: any) => {
          result.items.push({ id: name, name: name })
        });

        this._categories$.next(result.items);
      }
    });
    this._getTags().subscribe((result: any | undefined) => {
      if (result) {
        var emptyCategory = new CategoryModel();
        emptyCategory.id = "";
        emptyCategory.name = "Select Tag";
        result.items = [];
        result.items.push(emptyCategory);
        result.forEach((name: any) => {
          result.items.push({ id: name, name: name })
        });

        this._tags$.next(result.items);
      }
    });
  }

  get resultItems$() { return this._resultItems$.asObservable(); }

  protected _search(): Observable<{ count: number | undefined, items: BlogsBaseModel[] | undefined }> {

    this.validateStartIndex();

    if (this._state.categories && this._state.categories[0] === undefined) {
      this._state.categories = undefined;
    }
    if (this._state.tags && this._state.tags[0] === undefined) {
      this._state.tags = undefined;
    }
    this._state.sortColumn = this.titleCaseWord(this._state.sortColumnDirective) as string;

    return this.aiApiService.getBlogs(this._state);
  }

  clearListService() {
    this._state = { ...this.defaultBlogListState };
    this._resultItems$.next(new Array<BlogsBaseModel>());
    this._total$.next(0);
  }

  private _getCategories(): Observable<SearchResultModel<CategoryModel> | undefined> {
    var search = new CategorySearchModel();
    search.property = "";
    return this.aiApiService.getBlogCategories(search);
  }

  private _getTags(): Observable<SearchResultModel<CategoryModel> | undefined> {
    var search = new CategorySearchModel();
    search.property = "";
    return this.aiApiService.getBlogTags(search);
  }

  setViewedMostRecentInsight(userId: string, insightId: string): boolean {
    if (userId && insightId) {
      localStorage.setItem(userId, insightId);
      return true;
    }
    return false;
  }

  //this type of search is used when setting data ranges using the calender dropdown. This type of range search is currently not used anywhere else so its left as part of this class for now. Refactor this function if need to use this method else where later. 
  protected _setDateSearch(patch: Partial<BlogSearchModel>) {
    Object.assign(this._state, patch);
    if (this._state.rangeDate) {
      if (this._state.rangeDate.to && this._state.rangeDate.from) {
        this._state.to = this._state.rangeDate.to;
        this._state.from = this._state.rangeDate.from;
        this._search$.next();
      } else {
        this._state.to = new Date(this._state.rangeDate.from);
        this._state.to.setDate(this._state.to.getDate() + 1);
        this._state.from = this._state.rangeDate.from;
        this._search$.next();
      }
    } else {
      this._state.to = undefined;
      this._state.from = undefined;
      this._search$.next();
    }
  }

  getViewedMostRecentInsight(userId: string | undefined): string | null {
    try {
      var lsValue = null;

      if (userId) {
        lsValue = localStorage.getItem(userId);
      }

      return lsValue;
    } catch (error) {
      return null;
    }
  }

}
